import * as React from 'react';
import { connect } from 'react-redux';
import { Label } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../store';
import {
    DelegationScope,
    DelegationStatus,
    MyDelegation,
    actionCreators,
    getDelegationStatus
} from '../../../store/DelegationStore';
import { getTenantToday } from '../../../store/selectors/settingsSelectors';
import { toDate } from '../../utils/common';
import { Validator } from '../../../validation';
import InfoableComponent from '../../common/InfoableComponent/InfoableComponent';
import LabellableComponent from '../../common/LabellableComponent/LabellableComponent';
import PersonPickerInput, { PersonInfo } from '../../common/inputs/PersonPickerInput';
import DatePickerInput from '../../common/inputs/DatePickerInput';
import './DelegationModeSettings.css';

type OwnProps = {};
type StateProps = {
    delegation?: MyDelegation;
    tenantToday: Date;
};
type ActionProps = typeof actionCreators;
type Props = OwnProps & StateProps & ActionProps;

const infoHint = "Select one or more users to approve timesheets on your behalf. "
    + "Delegation will automatically start and expire according to the specified dates.";

const noUsersFound = "No users found. Only Direct Managers and Project Managers can approve timesheets on your behalf.";

const statusConfig: Record<DelegationStatus, { label: string; className: string }> = {
    [DelegationStatus.NotActive]: { label: "Not Active", className: "not-active" },
    [DelegationStatus.Pending]: { label: "Pending", className: "pending" },
    [DelegationStatus.Active]: { label: "Active", className: "active" },
    [DelegationStatus.Expired]: { label: "Expired", className: "expired" }
};

const DelegationModeSettings = (props: Props) => {
    const { delegation, tenantToday, loadMyDelegation, updateMyDelegation } = props;

    const [delegateUsers, setDelegateUsers] = React.useState<PersonInfo[]>([]);
    const [from, setFrom] = React.useState<string | undefined>();
    const [until, setUntil] = React.useState<string | undefined>();
    const seeded = React.useRef(false);

    React.useEffect(() => {
        loadMyDelegation(DelegationScope.TimesheetApproval);
    }, []);

    React.useEffect(() => {
        if (delegation && !seeded.current) {
            seeded.current = true;
            setDelegateUsers(delegation.delegateUsers);
            setFrom(delegation.from?.toDateOnlyString());
            setUntil(delegation.until?.toDateOnlyString());
        }
    }, [delegation]);

    const untilValidator = React.useMemo(
        () => Validator.new().dateIsGreaterThenOrEqual(from, "Invalid date").build(),
        [from]);

    const save = (users: PersonInfo[], newFrom?: string, newUntil?: string) => updateMyDelegation({
        scope: DelegationScope.TimesheetApproval,
        delegateUserIds: users.map(_ => _.id),
        from: newFrom,
        until: newUntil
    });

    const status = statusConfig[getDelegationStatus(
        { scope: DelegationScope.TimesheetApproval, delegateUsers, from: toDate(from), until: toDate(until) },
        tenantToday)];

    return <div className="delegation-mode-settings">
        <InfoableComponent description={infoHint}>
            <Label className="section-header">Delegation Mode Settings</Label>
        </InfoableComponent>
        <div className="delegation-status-label">Delegate my timesheet approval responsibility</div>
        <span className={`delegation-status ${status.className}`}>{status.label}</span>
        <LabellableComponent className="field-container" label="To the user(s)" required>
            <PersonPickerInput
                multichoice
                value={delegateUsers}
                searchUrl={`api/user/delegation/find?scope=${DelegationScope.TimesheetApproval}`}
                inputProps={{ placeholder: "Type to search" }}
                noResultsFoundText={noUsersFound}
                onChanged={(value: PersonInfo[] | null) => {
                    const users = value ?? [];
                    setDelegateUsers(users);
                    save(users, from, until);
                }} />
        </LabellableComponent>
        <LabellableComponent className="field-container" label="From the date" required>
            <DatePickerInput
                value={from}
                onChanged={(value: string | null) => {
                    setFrom(value ?? undefined);
                    save(delegateUsers, value ?? undefined, until);
                }} />
        </LabellableComponent>
        <LabellableComponent className="field-container" label="Until the date (inclusive)" required>
            <DatePickerInput
                value={until}
                minDate={from}
                validator={untilValidator}
                onChanged={(value: string | null) => {
                    setUntil(value ?? undefined);
                    save(delegateUsers, from, value ?? undefined);
                }} />
        </LabellableComponent>
    </div>;
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        delegation: state.delegation.myDelegations[DelegationScope.TimesheetApproval],
        tenantToday: getTenantToday(state)
    };
}

export default connect(mapStateToProps, actionCreators)(DelegationModeSettings);
